/**
 * @description Renders the per-route SEO head tags: title, description, keywords,
 *              robots, canonical and the Open Graph set.
 *
 *              Replaces the single hard-coded homepage <Helmet> that used to sit in
 *              App.render(), which stamped the homepage title and canonical onto every
 *              route. Copy comes from `constants/page-meta.ts`.
 *
 *              Person and company profiles build their tags from fetched data, so
 *              `getPageMeta` returns undefined for them and this component falls back to a
 *              self-canonical `noindex` placeholder that their controllers override once the
 *              profile resolves. It must render *something*: react-helmet removes every tag
 *              it owns as soon as no mounted instance declares one.
 * @filename page-meta.tsx
 */

import * as React from "react";
import { Helmet } from "react-helmet";
import { canonicalUrlForPath, getPageMeta, UNRESOLVED_PROFILE_META } from "../../constants/page-meta";
import { OG_IMAGE_URL } from "../../constants/site-urls";

interface PageMetaTagsProps {
  /** Route path to render tags for, e.g. "/about". */
  pathname: string;
}

export const PageMetaTags = (props: PageMetaTagsProps): React.ReactElement => {
  const { pathname } = props;
  const meta = getPageMeta(pathname) || UNRESOLVED_PROFILE_META;
  const canonicalUrl = canonicalUrlForPath(pathname);

  return (
    <Helmet>
      <title data-react-helmet="true">{meta.title}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <meta name="robots" content={meta.robots} />
      <meta property="og:title" data-react-helmet="true" content={meta.title} />
      <meta property="og:description" data-react-helmet="true" content={meta.description} />
      <meta property="og:url" data-react-helmet="true" content={canonicalUrl} />
      <meta name="image" property="og:image" data-react-helmet="true" content={OG_IMAGE_URL} />
      <meta property="og:image:type" data-react-helmet="true" content="image/png" />
      <meta property="og:type" data-react-helmet="true" content="website" />
      <meta property="og:site_name" data-react-helmet="true" content="Radivision" />
    </Helmet>
  );
};

export default PageMetaTags;
